import { Schema } from '../schema.js';
import { EnumType, ListType, ObjectType, PrimitiveType } from '../types.js';
import { deepCopy } from '../deep-copy.js';
import { pModelTypeGuard, pOptionTypeGuard, ValidationResult } from '../engine.js';
import { PropertyPathWalker } from './PropertyPathWalker.js';
import { engineUtils } from '../services/EngineUtils.js';
export class SinglePropertyMutator {
    constructor(propertyPath, value, opts) {
        this.propertyPath = propertyPath;
        this.value = value;
        this.opts = opts;
    }
    get notify() {
        return this.context.notifications.autoCorrectModelPublisher;
    }
    ;
    mutate(context, typeRef, model) {
        let prop = new PropertyPathWalker(this.propertyPath, context.schema, model, typeRef).getPropertyValue();
        if (prop) {
            this.updateExecutingContext(context, typeRef, model, prop);
            return this.mutateProperty();
        }
        else {
            return [model, ValidationResult.Error('invalid property in path:' + this.propertyPath)];
        }
    }
    updateExecutingContext(context, typeRef, model, prop) {
        this.schema = context.schema;
        this.context = context;
        this.typeRef = typeRef;
        this.model = model;
        this.prop = prop;
    }
    mutateProperty() {
        if (!this.prop.parent || !this.prop.parent.value) {
            return [this.model, ValidationResult.Error('property parent is Nil in path:' + this.propertyPath)];
        }
        let type = this.schema.getType(this.prop.property.type);
        if (type instanceof ListType) {
            return this.mutateListProperty();
        }
        else if (type instanceof ObjectType) {
            return this.mutateObjectProperty();
        }
        else if (type instanceof EnumType) {
            return this.mutateEnumProperty(type.values);
        }
        else if (type instanceof PrimitiveType) {
            return this.mutatePrimitiveProperty(type);
        }
        else {
            throw new Error(`Unknown type: ${type}`);
        }
    }
    mutateObjectProperty() {
        if (pOptionTypeGuard(this.value) || pModelTypeGuard(this.value)) {
            let value = pOptionTypeGuard(this.value) ? this.value.model : this.value;
            if (!SinglePropertyMutator.skipOptionCopyOnMutate) {
                value = deepCopy(value, [], true);
            }
            return this.safelyUpdateModelProperty(value);
        }
        else {
            return [this.model, ValidationResult.Error('property \'' + this.prop.property.name + '\' type can be only simple type at the end of path:' + this.propertyPath)];
        }
    }
    mutateEnumProperty(possibleElements) {
        if (!possibleElements.includes(this.value)) {
            return [this.model, ValidationResult.Error('property:' + this.propertyPath + ' can\'t be set to:' + this.value)];
        }
        return this.safelyUpdateModelProperty(this.value);
    }
    mutatePrimitiveProperty(type) {
        switch (type) {
            case Schema.string_type:
            case Schema.ml_string_type:
                for (let constraint of this.prop.property.opts.constraints || []) {
                    let c = this.schema.getConstraint(constraint.name, constraint.kwargs);
                    if (!c.valid(this.value)) {
                        return [this.model, ValidationResult.Error('constraint violation for value:' + this.value + ' in path:' + this.propertyPath)];
                    }
                }
                return this.safelyUpdateModelProperty(this.value);
            case Schema.number_type:
            case Schema.big_number_type:
            case Schema.boolean_type:
                return this.safelyUpdateModelProperty(this.value);
        }
    }
    mutateListProperty() {
        if (!Array.isArray(this.value)) {
            throw new Error(`Invalid value type for ${this.value}. List expected for "${this.propertyPath}"`);
        }
        const value = this.value.concat().map(o => deepCopy(o.model, [], true));
        return this.safelyUpdateModelProperty(value);
    }
    safelyUpdateModelProperty(value) {
        this.updateModelProperty(this.prop, value);
        if (this.isModelNeedAutoCorrection()) {
            return this.autoCorrectModel();
        }
        else {
            return [this.model, ValidationResult.Valid()];
        }
    }
    updateModelProperty(propValue, value) {
        propValue.parent.value[propValue.property.name] = value;
    }
    isModelNeedAutoCorrection() {
        var _a;
        if (((_a = this.opts) === null || _a === void 0 ? void 0 : _a.autoCorrectPropertyPath) == null) {
            return false;
        }
        const validationResult = this.context.validate(this.model);
        return validationResult.isInvalid;
    }
    autoCorrectModel() {
        const availableAutoCorrectPropertyValues = this.getAvailableAutoCorrectPropertyValues();
        const originAutoCorrectPropertyValue = this.getAutoCorrectPropertyValue();
        for (let correctValue of availableAutoCorrectPropertyValues) {
            const isCorrected = this.correctModelWith(correctValue);
            if (isCorrected) {
                const correctValueModel = this.getAutoCorrectPropertyValue();
                this.notify.propertyAutoCorrected(this.model, this.opts.autoCorrectPropertyPath, originAutoCorrectPropertyValue, correctValueModel);
                return [this.model, ValidationResult.Valid()];
            }
        }
        return [this.model, ValidationResult.Error(`Cannot autocorrect model for ${this.opts.autoCorrectPropertyPath}`)];
    }
    getAvailableAutoCorrectPropertyValues() {
        const propertyValues = this.context.propertyValuesByPath(this.model, this.opts.autoCorrectPropertyPath);
        return (propertyValues.map(v => typeof v).every(v => ['number', 'string'].includes(v)))
            ? propertyValues
            : engineUtils.flattenOptions(propertyValues);
    }
    getAutoCorrectPropertyValue() {
        const walker = new PropertyPathWalker(this.opts.autoCorrectPropertyPath, this.context.schema, this.model, this.typeRef);
        const propValue = walker.getPropertyValue();
        return propValue.value;
    }
    correctModelWith(correctValue) {
        const mutator = new SinglePropertyMutator(this.opts.autoCorrectPropertyPath, correctValue);
        const [model, validationResult] = mutator.mutate(this.context, this.typeRef, this.model);
        const isCorrected = validationResult.isValid && this.context.validate(this.model).isValid;
        if (isCorrected) {
            this.model = model;
        }
        return isCorrected;
    }
}
SinglePropertyMutator.skipOptionCopyOnMutate = false;
//# sourceMappingURL=SinglePropertyMutator.js.map